/**
 * ShareAPI - Service for sharing compositions
 */

import { APIService, APIServiceError } from './APIService';
import type { ShareRequest, ShareResponse } from '../../types/api';
import type { Composition } from '../../types/composition';

/**
 * API endpoint for sharing
 */
const SHARE_API_URL =
  import.meta.env.VITE_API_SHARE_URL ||
  'https://egght2t2zl.execute-api.us-west-2.amazonaws.com/test/share';

/**
 * ShareAPI service for composition sharing
 */
export class ShareAPI extends APIService {
  /**
   * Share a composition and get a shareable URL
   * 
   * @param composition - Composition to share
   * @returns Share response with URL and ID
   * @throws APIServiceError if sharing fails
   */
  async share(composition: Composition): Promise<ShareResponse> {
    if (!composition || !composition.channels) {
      throw new APIServiceError('Invalid composition', 400);
    }

    const request: ShareRequest = {
      composition,
    };

    try {
      const response = await this.fetchWithRetry(SHARE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        retries: 2,
        timeout: 15000,
      });

      const result = await response.json();
      console.log('[ShareAPI] Received response:', result);

      // Handle different response formats from API
      // API returns: { shareUrl: "https://..." }
      // We need: { url: "https://...", id: "..." }
      if (!result || (!result.url && !result.shareUrl)) {
        throw new APIServiceError(
          'Invalid response format - missing URL',
          500
        );
      }

      // Extract URL (handle both formats)
      const shareUrl = result.shareUrl || result.url;
      
      // Extract ID from URL if not provided directly
      // URL format: https://8bitcomposer.com/share/{id}
      let shareId = result.id;
      if (!shareId && shareUrl) {
        const urlParts = shareUrl.split('/');
        shareId = urlParts[urlParts.length - 1];
      }

      if (!shareId) {
        throw new APIServiceError(
          'Invalid response format - could not extract ID',
          500
        );
      }

      return {
        url: shareUrl,
        id: shareId,
        expiresAt: result.expiresAt,
      } as ShareResponse;
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }

      throw new APIServiceError(
        `Sharing failed: ${(error as Error).message}`,
        500
      );
    }
  }

  /**
   * Load a shared composition by ID
   * 
   * @param id - Composition ID
   * @returns Composition
   * @throws APIServiceError if loading fails
   */
  async load(id: string): Promise<Composition> {
    if (!id) {
      throw new APIServiceError('Composition ID is required', 400);
    }

    try {
      // Load directly from S3 bucket
      const url = `https://8bitcomposer-shared.s3.us-west-2.amazonaws.com/${id}.json`;
      
      const response = await this.fetchWithRetry(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        retries: 2,
        timeout: 10000,
      });

      const result = await response.json();

      // Validate response format
      if (!result || !result.channels) {
        throw new APIServiceError(
          'Invalid composition format',
          500
        );
      }

      return result as Composition;
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }

      throw new APIServiceError(
        `Failed to load composition: ${(error as Error).message}`,
        500
      );
    }
  }
}

// Export singleton instance
export const shareAPI = new ShareAPI();
